import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import formatMeasurementData, {
    BodyComposition,
    formatWellnessData,
    mapBodyCompToModel,
    Measurement,
    SizeMatchRecommendation,
} from '../services/DataFormatService';

// Define interfaces for type safety
interface Config {
    serverUrlUS: string;
    serverUrlAU: string;
}

interface SessionResponse {
    bodyMeasurements?: { measurements: Measurement[]; };
    bodyComposition?: { measurements: Measurement[]; };
    wellnessMetrics?: { measurements: Measurement[]; };
    sizeMatchRecommendations?: SizeMatchRecommendation[];
    merchantName?: string;
    frontProfileImageUrl?: string;
    sideProfileImageUrl?: string;
}

interface SessionData {
    isError: boolean;
    error: Error | null;
    isLoading: boolean;
    isScanPending: boolean;
    measurements: ReturnType<typeof formatMeasurementData>;
    hasMeasurements: boolean;
    wellnessData: ReturnType<typeof formatWellnessData>;
    hasWellness: boolean;
    sizeMatchRecommendations: SizeMatchRecommendation[];
    hasSizeMatch: boolean;
    bodyComposition: BodyComposition | undefined;
    hasBodyComposition: boolean;
    merchantName: string | undefined;
    frontProfileImageUrl: string | null;
    sideProfileImageUrl: string | null;
}

const configFile = './config.json';

const fetchConfig = async (): Promise<Config> => {
    const response = await fetch(configFile);
    if (!response.ok) {
        throw new Error('Failed to fetch config');
    }
    return response.json();
};

function filterUniqueRecommendations(recommendations: SizeMatchRecommendation[]): SizeMatchRecommendation[] {
    return recommendations.filter((value, index, self) => index === self.findIndex((t) => (
        t.productCode === value.productCode
        && t.gender === value.gender
    )));
}

const fetchSessionData = async (sessionId: string, region: string | null): Promise<SessionResponse> => {
    const config = await fetchConfig();
    const serverUrl = region === 'us' ? config.serverUrlUS : config.serverUrlAU;
    const { data, status } = await axios.get<SessionResponse>(`${serverUrl}?sessionId=${sessionId}`);
    if (status === 202) {
        throw new Error('NotReady');
    }
    return data;
};

function useSessionData(sessionId: string, region: string | null): SessionData {
    const { data, isError, error, isLoading, failureReason } = useQuery({
        queryKey: ['session', sessionId, region],
        queryFn: () => fetchSessionData(sessionId!, region),
        enabled: !!sessionId,
        retry: (_failureCount: number, retryError: Error) => retryError.message === 'NotReady',
        retryDelay: () => 10000,
        staleTime: Infinity,
        gcTime: Infinity,
        refetchOnWindowFocus: true,
    });

    const isScanPending = failureReason?.message === 'NotReady';

    // Process data only when available
    const measurements = data?.bodyMeasurements?.measurements
        ? formatMeasurementData(data.bodyMeasurements.measurements)
        : [];
    const hasMeasurements = !!measurements?.length;

    const wellnessData = data?.wellnessMetrics ? formatWellnessData(data.wellnessMetrics?.measurements ?? []) : {};
    const hasWellness = !!wellnessData && Object.keys(wellnessData).length > 0;

    const sizeMatchRecommendations = data?.sizeMatchRecommendations ? filterUniqueRecommendations(data.sizeMatchRecommendations) : [];
    const hasSizeMatch = sizeMatchRecommendations.length > 0;

    const bodyComposition = mapBodyCompToModel(data?.bodyComposition?.measurements ?? []);
    const hasBodyComposition = !!data?.bodyComposition?.measurements?.length;

    return {
        isError,
        error,
        isLoading,
        isScanPending,
        measurements,
        hasMeasurements,
        wellnessData,
        hasWellness,
        sizeMatchRecommendations,
        hasSizeMatch,
        bodyComposition,
        hasBodyComposition,
        merchantName: data?.merchantName ?? undefined,
        frontProfileImageUrl: data?.frontProfileImageUrl ?? null,
        sideProfileImageUrl: data?.sideProfileImageUrl ?? null,
    };
}

export default useSessionData;
